// reducer.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { clearCartStorage } from "./thunk";
import { CartItem } from "types";

interface CartState {
  items: CartItem[];
  totalPrice: number;
  payment_method: string;
  customer_id: string;
  order_id: string;
}

const initialState: CartState = {
  items: [],
  totalPrice: 0,
  payment_method: "",
  customer_id: "",
  order_id: "",
};

const calculateTotalPrice = (items: CartItem[]) => {
  return items.reduce((total, item) => total + item.price * item.quantity, 0);
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.items.find(
        (item) => item.id === action.payload.id
      );
      if (existingItem) {
        existingItem.quantity = Number(existingItem.quantity) + 1;
      } else {
        state.items.push({
          ...action.payload,
        });
      }
      state.totalPrice = calculateTotalPrice(state.items);
    },

    updateItem: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.items.find(
        (item) => item.id === action.payload.id
      );
      if (existingItem) {
        existingItem.quantity = action.payload.quantity;
        if (existingItem.quantity < 0) {
          state.items = state.items.filter(
            (item) => item.id !== action.payload.id
          );
        }
      } else {
        state.items.push({
          ...action.payload,
          quantity: action.payload.quantity,
        });
      }
      state.totalPrice = calculateTotalPrice(state.items); // Update total price after adding an item
    },

    updateItemDiscount: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.items.find(
        (item) => item.id === action.payload.id
      );
      if (existingItem) {
        existingItem.discount_amt = action.payload.discount_amt;
        existingItem.discount_percentage = action.payload.discount_percentage;
      } else {
        state.items.push({
          ...action.payload,
          quantity: action.payload.quantity,
        });
      }
      state.totalPrice = calculateTotalPrice(state.items); // Update total price after adding an item
    },

    removeItem: (state, action: PayloadAction<string>) => {
      const existingItem = state.items.find(
        (item) => item.id === action.payload
      );
      if (existingItem) {
        existingItem.quantity = Number(existingItem.quantity) - 1;
        if (existingItem.quantity <= 0) {
          state.items = state.items.filter(
            (item) => item.id !== action.payload
          );
        }
        state.totalPrice = calculateTotalPrice(state.items);
      }
    },
    deleteItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      state.totalPrice = calculateTotalPrice(state.items);
    },
    clearCart: (state) => {
      state.items = [];
      state.totalPrice = 0; // Reset total price when the cart is cleared
    },
    setCustomerId: (state, action: PayloadAction<string>) => {
      state.customer_id = action.payload;
    },
    clearCustomerId: (state) => {
      state.customer_id = "";
    },
    setOrderId: (state, action: PayloadAction<string>) => {
      state.order_id = action.payload;
    },
    clearOrderId: (state) => {
      state.order_id = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearCartStorage.fulfilled, (state) => {
      state.items = [];
      state.totalPrice = 0; // Clear total price when storage is cleared
    });
  },
});

export const {
  addItem,
  removeItem,
  deleteItem,
  clearCart,
  updateItem,
  updateItemDiscount,
  setCustomerId,
  clearCustomerId,
  setOrderId,
  clearOrderId,
} = cartSlice.actions;
export default cartSlice.reducer;
